import { api } from './api';
import { Prova, ProvaComEstatisticas, CreateProvaData, ProvaAcesso, ProvaQuestao, CreateProvaQuestaoData, ResultadoCorrecao, EstatisticasCorrecao, CorrecaoManual } from '../types/provas';

interface ListProvasResponse {
  data: ProvaComEstatisticas[];
}

interface CreateProvaResponse {
  data: Prova;
}

interface ProvaResponse {
  data: Prova;
}

interface ListAcessosResponse {
  data: ProvaAcesso[];
}

interface ListQuestoesResponse {
  data: ProvaQuestao[];
}

interface CreateQuestaoResponse {
  data: ProvaQuestao;
}

class ProvasService {
  async listarPorGrupoComEstatisticas(id_grupo: string): Promise<ProvaComEstatisticas[]> {
    const response = await api.get<ListProvasResponse>(`/grupos/${id_grupo}/provas/estatisticas`);
    return response.data.data || response.data as any;
  }

  async criar(data: CreateProvaData): Promise<Prova> {
    const { id_grupo, ...rest } = data;
    const response = await api.post<CreateProvaResponse>(`/grupos/${id_grupo}/provas`, rest);
    return response.data.data || response.data as any;
  }

  async buscarPorId(id: string): Promise<Prova> {
    const response = await api.get<ProvaResponse>(`/grupos/provas/${id}`);
    return response.data.data || response.data as any;
  }

  async atualizar(id: string, data: Partial<CreateProvaData>): Promise<Prova> {
    const response = await api.put<ProvaResponse>(`/grupos/provas/${id}`, data);
    return response.data.data || response.data as any;
  }

  async excluir(id: string): Promise<void> {
    await api.delete(`/grupos/provas/${id}`);
  }

  async divulgarResultado(id: string): Promise<void> {
    await api.patch(`/grupos/provas/${id}/divulgar-resultado`);
  }

  async alternarStatus(id: string): Promise<Prova> {
    const response = await api.patch<ProvaResponse>(`/grupos/provas/${id}/alternar-status`);
    return response.data.data || response.data as any;
  }

  async listarMinhasProvas(): Promise<ProvaAcesso[]> {
    const response = await api.get<ListAcessosResponse>(`/provas/minhas-provas`);
    return response.data.data || response.data as any;
  }

  async listarMinhasProvasPorGrupo(id_grupo: string): Promise<ProvaAcesso[]> {
    const response = await api.get<ListAcessosResponse>(`/grupos/${id_grupo}/minhas-provas`);
    return response.data.data || response.data as any;
  }

  // Métodos para questões
  async criarQuestao(data: CreateProvaQuestaoData): Promise<ProvaQuestao> {
    const { id_prova, ...rest } = data;
    const response = await api.post<CreateQuestaoResponse>(`/grupos/provas/${id_prova}/questoes`, rest);
    return response.data.data || response.data as any;
  }

  async listarQuestoes(id_prova: string): Promise<ProvaQuestao[]> {
    const response = await api.get<ListQuestoesResponse>(`/grupos/provas/${id_prova}/questoes`);
    return response.data.data || response.data as any;
  }

  async atualizarQuestao(id_questao: string, data: Partial<CreateProvaQuestaoData>): Promise<ProvaQuestao> {
    const response = await api.put<CreateQuestaoResponse>(`/grupos/provas/questoes/${id_questao}`, data);
    return response.data.data || response.data as any;
  }

  async excluirQuestao(id_questao: string): Promise<void> {
    await api.delete(`/grupos/provas/questoes/${id_questao}`);
  }

  // Métodos para correção
  async processarCorrecaoAutomatica(id_acesso: string, opcoes: {
    processar_multipla_escolha?: boolean;
    processar_texto_livre?: boolean;
  } = {}): Promise<EstatisticasCorrecao> {
    const response = await api.post(`/provas/acessos/${id_acesso}/correcao-automatica`, opcoes);
    return response.data.data || response.data as any;
  }

  async processarCorrecaoManual(correcao: CorrecaoManual): Promise<any> {
    const response = await api.post('/provas/correcao-manual', correcao);
    return response.data.data || response.data as any;
  }

  async processarCorrecaoLote(id_acesso: string, correcoes: CorrecaoManual[]): Promise<EstatisticasCorrecao> {
    const response = await api.post(`/provas/acessos/${id_acesso}/correcao-lote`, { correcoes });
    return response.data.data || response.data as any;
  }

  async listarResultadosParaCorrecao(id_acesso: string): Promise<ResultadoCorrecao[]> {
    const response = await api.get(`/provas/acessos/${id_acesso}/resultados`);
    return response.data.data || response.data as any;
  }

  async calcularEstatisticasCorrecao(id_acesso: string): Promise<EstatisticasCorrecao> {
    const response = await api.get(`/provas/acessos/${id_acesso}/estatisticas`);
    return response.data.data || response.data as any;
  }

  async finalizarCorrecao(id_acesso: string): Promise<void> {
    await api.post(`/provas/acessos/${id_acesso}/finalizar-correcao`);
  }

  // Métodos para gerenciar acessos
  async concederAcessos(id_prova: string, militares_ids: string[]): Promise<ProvaAcesso[]> {
    const response = await api.post(`/grupos/provas/${id_prova}/acessos`, { militares_ids });
    return response.data.data || response.data as any;
  }

  async listarAcessosPorProva(id_prova: string): Promise<ProvaAcesso[]> {
    const response = await api.get(`/grupos/provas/${id_prova}/acessos`);
    return response.data.data || response.data as any;
  }

  async revogarAcesso(id_acesso: string): Promise<void> {
    await api.delete(`/grupos/provas/acessos/${id_acesso}`);
  }

  // Métodos para divulgação de resultados
  async listarResultadosFinalizados(id_prova: string): Promise<any[]> {
    const response = await api.get(`/provas/${id_prova}/resultados-finalizados`);
    return response.data.data || response.data as any;
  }

  async divulgarResultadoIndividual(id_acesso: string): Promise<void> {
    await api.put(`/provas/acessos/${id_acesso}/divulgar-resultado`);
  }

  async duplicarProva(id_prova: string): Promise<any> {
    const response = await api.post(`/provas/${id_prova}/duplicar`);
    return response.data;
  }
}

export const provasService = new ProvasService();
